import { DeliveryListFilter, DeliveryLogisticItem, DeliveryLogisticItemEntry, DeliverySchedule } from "../../types/delivery";
import { Order, OrderProduct } from "../../types/order";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import deliveryCore from "../../core/delivery.core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DeleteIcon from "../../icons/delete.icon";
import { useTranslation } from "../../core/translation.core";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { useDraggable } from "@dnd-kit/core";
import Accordion from "../common/accordion.comp";
import { addDays, format } from "date-fns";
import Link from "next/link";
import shipCore from "../../core/ship.core";
import { RefObject, useMemo, useRef, useState } from "react";
import SolidWeakBtn from "../form/solidWeakBtn.comp";
import { FancyCheckbox, LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";
import useDeliveryVehichles from "../../hooks/useDeliveryVehiclesList.hook";
import DeliveryNoteEditor from "./deliveryNoteEditor";
import DeliveryRecycleToggle from "./deliveryRecycleToggle";
import DeliveryFrontDoorToggle from "./deliveryFrontDoorToggle";
import { formatCurrency } from "../../core/currency.core";
import GiftIcon from "../../icons/gift.icon";
import { BundleProductBadge } from "./bundleProductBadge";
import React from "react";
import { FinalizeScheduleForm, LogisticShippingEditorForm } from "./logisticShippingEditor.comp";
import { toast } from "react-toastify";

type AssignFormValues = {
  assignMode: "prepared" | "draft" | "append";
  orderItemIds: string[];
  shipDate: string;
  vehicleId: string;
  appendTrackingId: string;
};

export default function ProcessingOrders({ filterMethods }: { filterMethods: UseFormReturn<DeliveryListFilter> }) {
  const { t, msg } = useTranslation();

  const { data, isPending, isError, failureReason } = usePendingLogisticsItems(filterMethods);

  if (isPending) return <LoadingOvalCenterIcon />;
  if (isError) return <ErrorCenterIcon reason={failureReason} />;

  const orders = data?.orders ?? [];
  const preparingSchedules = data?.preparingSchedules ?? [];

  return (
    <section data-type="section" className="basis-3/5 lg:order-1 max-h-[calc(100vh-17rem)] overflow-y-auto">
      {preparingSchedules.length > 0 && (
        <div data-preparing-schedules className="mb-8 space-y-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t(msg.delivery.preparingBatches)}</h3>
          {preparingSchedules.map((schedule) => (
            <PreparingScheduleCard key={String(schedule.id)} schedule={schedule} />
          ))}
        </div>
      )}
      <div className="space-y-8 gap-6">
        {orders.length > 0 ? (
          orders.map((order) => <OrderRow key={order.id} order={order} />)
        ) : (
          <p>{t(msg.delivery.noPendingOrdersForDelivery)}</p>
        )}
      </div>
    </section>
  );
}

function PreparingScheduleCard({ schedule }: { schedule: DeliverySchedule }) {
  return (
    <div
      data-preparing-schedule-id={schedule.id}
      className="border border-her-blue/30 rounded-lg p-4 dark:border-gray-600 dark:bg-her-darkSectionBg"
    >
      <ul data-preparing-schedule-payload className="text-xs mt-2 space-y-3 text-gray-600 dark:text-gray-400">
        {schedule.payload.map((p) => (
          <li key={p.trackingId} data-preparing-batch-tracking-id={p.trackingId} className="space-y-2">
            <PreparingScheduleTrackingHeader logisticItem={p} />
            <ul className="pl-2 space-y-2 border-l border-her-blue/20 dark:border-gray-600">
              {p.items.map((entry: DeliveryLogisticItemEntry) => (
                <PreparingScheduleLineItem
                  key={`${p.trackingId}-${entry.orderProductId}`}
                  trackingId={p.trackingId}
                  entry={entry}
                />
              ))}
            </ul>
            <div className="mt-2 space-y-2" data-preparing-batch-shipping>
              <LogisticShippingEditorForm logisticItem={p} />
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <FinalizeScheduleForm schedule={schedule} />
      </div>
    </div>
  );
}

function PreparingScheduleTrackingHeader({ logisticItem }: { logisticItem: DeliveryLogisticItem }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  async function removeBatch() {
    try {
      await deliveryCore.removeLogisticItem({ trackingId: logisticItem.trackingId });
      queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
      queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
      queryClient.invalidateQueries({ queryKey: ["deliveryAppendTargets"] });
      toast.success(t(msg.import.success));
    } catch {
      toast.error(t(msg.errors.contactAdmin));
    }
  }

  return (
    <div className="flex justify-between items-center gap-2 font-medium text-gray-800 dark:text-gray-200">
      <span>
        {logisticItem.trackingId} — {logisticItem.items.length} {t(msg.delivery.items)}
      </span>
      <button
        type="button"
        data-role="preparing-remove-tracking"
        className="text-red-400 shrink-0 p-1 rounded-lg hover:bg-red-500/10 dark:text-red-400"
        onClick={removeBatch}
      >
        <DeleteIcon className="w-5 h-5" />
      </button>
    </div>
  );
}

function preparingScheduleLineTitle(entry: DeliveryLogisticItemEntry): string {
  const titled = entry.productName?.trim() || entry.name?.trim() || entry.sku?.trim();
  if (titled) return titled;
  return String(entry.orderProductId);
}

function PreparingScheduleLineItem({ trackingId, entry }: { trackingId: string; entry: DeliveryLogisticItemEntry }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const orderId = String(entry.orderId);
  const label = preparingScheduleLineTitle(entry);
  const qty = entry.quantity ?? 0;

  async function removeLine() {
    try {
      await deliveryCore.removeLogisticOrderItem({
        trackingId,
        orderProductId: String(entry.orderProductId),
      });
      queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
      queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
      queryClient.invalidateQueries({ queryKey: ["deliveryAppendTargets"] });
      toast.success(t(msg.import.success));
    } catch {
      toast.error(t(msg.errors.contactAdmin));
    }
  }

  return (
    <li data-preparing-line-order-product-id={entry.orderProductId} className="text-sm text-gray-700 dark:text-gray-300">
      <div className="flex justify-between items-start gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          {entry.orderNumber ? (
            <Link className="text-her-blue shrink-0" href={`/order/${orderId}`} target="_blank">
              # {entry.orderNumber}
            </Link>
          ) : null}
          <span className="flex flex-wrap items-center gap-2 min-w-0">
            {label}
            <span className="text-gray-600 dark:text-gray-400 shrink-0">x{qty}</span>
            {entry.isGiftProduct ? <GiftIcon className="text-her-paradisePink w-5 h-5 shrink-0" /> : null}
            {entry.productType === "bundle" ? (
              <BundleProductBadge orderProduct={{ productType: "bundle" } as OrderProduct} />
            ) : null}
          </span>
        </div>
        <button
          type="button"
          data-role="preparing-remove-line"
          className="text-red-400 shrink-0 p-1 rounded-lg hover:bg-red-500/10 dark:text-red-400"
          onClick={removeLine}
        >
          <DeleteIcon className="w-5 h-5" />
        </button>
      </div>
      {entry.productType === "bundle" && entry.bundleProducts && entry.bundleProducts.length > 0 ? (
        <div className="ml-2 mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs text-gray-600 dark:text-gray-400">
          {entry.bundleProducts.map((bp) => (
            <React.Fragment key={bp.orderProductId}>
              <span data-name={bp.name}>{bp.name}</span>
              <span>x {bp.quantity}</span>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function usePendingLogisticsItems(filterMethods: UseFormReturn<DeliveryListFilter>) {
  const [regions, districts, subDistricts, productIds, warehouseIds, shipOptionIds] = useWatch({
    control: filterMethods.control,
    name: ["regions", "districts", "subDistricts", "productIds", "warehouseIds", "shipOptionIds"],
  }) as any[];

  return useQuery({
    queryKey: ["pendingLogisticItems", regions, districts, subDistricts, productIds, warehouseIds, shipOptionIds],
    queryFn: async () => {
      const raw = await deliveryCore.listPendingItems({
        regions,
        districts,
        subDistricts,
        productIds,
        warehouseIds,
        supplierIds: [],
        customerIds: [],
        shipOptionIds,
        includePreparingSchedules: true,
        date: {
          from: format(addDays(new Date(), -365), "yyyy-MM-dd"),
          to: format(new Date(), "yyyy-MM-dd"),
        },
        paging: {
          page: 1,
          size: 50,
        },
      });
      if (Array.isArray(raw)) {
        return { orders: raw, preparingSchedules: [] as DeliverySchedule[] };
      }
      return raw;
    },
  });
}

function OrderRow({ order }: { order: Order }) {
  const { t, msg } = useTranslation();

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `${order.id}`,
    data: {
      orderItemIds: order.products.map((op: OrderProduct) => op.orderProductId),
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  if (!order) return null;

  const orderProducts = order.products;

  return (
    <div className="delivery-pending-row relative">
      <div className="w-full gap-4 mb-4">
        <Link className="basis-[120px] shrink-0 text-her-blue" href={`/order/${order.id}`} target="_blank">
          # {order.orderNumber}
        </Link>
        <div className="absolute right-0 top-0">
          <AssignButton order={order} />
        </div>
        <p>
          {`${order.user.firstName} ${order.user.lastName}`} / {order.user.mobile} / {order.shipAddr?.address}
        </p>
        <p>
          {t(msg.ship.district)}: {order.shipAddr?.district}
        </p>
        <ShippingMethod order={order} />
      </div>

      <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
        <Accordion title={`${orderProducts.length} ${t(msg.order.orderItems)}`}>
          <PendingDeliveryOrderItems order={order} />
        </Accordion>
      </div>

      <div className="mt-4 p-3 bg-gray-50 dark:bg-her-darkSectionBg rounded-md space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-1">
          {t(msg.delivery.deliveryOptions)}
        </h4>
        <DeliveryNoteEditor order={order} />
        <DeliveryRecycleToggle order={order} />
        <DeliveryFrontDoorToggle order={order} />
      </div>

      <div className="flex justify-center">
        <hr className="mt-6 w-3/4 text-center items-center border-her-aaa border" />
      </div>
    </div>
  );
}

function ShippingMethod({ order }: { order: Order }) {
  const { t, msg } = useTranslation();
  const { data: shipSettings } = useQuery({
    queryKey: ["delivery-shipOptions"],
    queryFn: () => shipCore.getShipSettings(),
  });

  if (!shipSettings) return null;

  const shipOption = shipSettings.flatMap((s) => s.options).find((shipOption) => shipOption.id === order.shipId);

  return (
    <p>
      {t(msg.ship.shippingMethod)}: {shipOption?.name}
    </p>
  );
}

function AssignButton({ order }: { order: Order }) {
  const { t, msg } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formKey, setFormKey] = useState(0);
  const { data: vehicles } = useDeliveryVehichles();
  const methods = useForm<AssignFormValues>({
    defaultValues: {
      assignMode: "prepared",
      orderItemIds: order.products.map((p) => p.orderProductId),
      shipDate: format(new Date(), "yyyy-MM-dd"),
      vehicleId: "",
      appendTrackingId: "",
    },
  });

  return (
    <>
      <button
        type="button"
        className="btn-assign bg-her-blue px-4 py-2 rounded-md text-white"
        onClick={() => {
          methods.reset({
            assignMode: "prepared",
            orderItemIds: order.products.map((p) => p.orderProductId),
            shipDate: format(new Date(), "yyyy-MM-dd"),
            vehicleId: vehicles?.[0]?.id ?? "",
            appendTrackingId: "",
          });
          setFormKey((k) => k + 1);
          dialogRef.current?.showModal();
        }}
      >
        {t(msg.delivery.assign)}
      </button>
      <AssignDialog dialogRef={dialogRef} order={order} methods={methods} formKey={formKey} />
    </>
  );
}

function AssignDialog({
  dialogRef,
  order,
  methods,
  formKey,
}: {
  dialogRef: RefObject<HTMLDialogElement>;
  order: Order;
  methods: UseFormReturn<AssignFormValues>;
  formKey: number;
}) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const { data: vehicles } = useDeliveryVehichles();

  const assignMode = methods.watch("assignMode");

  const { data: appendSchedules } = useQuery({
    queryKey: ["deliveryAppendTargets"],
    queryFn: () => deliveryCore.listSchedules("all"),
    enabled: assignMode === "append",
  });

  const appendOptions = useMemo(() => {
    if (!appendSchedules) return [];
    return appendSchedules.flatMap((s) =>
      s.payload.map((p) => ({
        value: p.trackingId,
        label: `${p.trackingId} · ${s.shipDate || t(msg.delivery.draftScheduleLabel)}`,
      }))
    );
  }, [appendSchedules, t, msg.delivery.draftScheduleLabel]);

  async function assign(data: AssignFormValues) {
    const orderItemIds = data.orderItemIds;
    if (!orderItemIds?.length) {
      toast.error(t(msg.general.noneSelected));
      return;
    }
    if (data.assignMode === "prepared") {
      if (!data.shipDate || !data.vehicleId) {
        toast.error(t(msg.delivery.selectOrderItemsAndVehicleToContinue));
        return;
      }
      await deliveryCore.createSchedule({
        orderItemIds,
        shipDate: data.shipDate,
        vehicleId: data.vehicleId,
      });
    } else if (data.assignMode === "draft") {
      await deliveryCore.createSchedule({ orderItemIds });
    } else {
      if (!data.appendTrackingId) {
        toast.error(t(msg.delivery.selectBatch));
        return;
      }
      await deliveryCore.appendDelivery({
        trackingId: data.appendTrackingId,
        orderItemIds,
      });
    }

    queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
    queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
    queryClient.invalidateQueries({ queryKey: ["deliveryAppendTargets"] });
    dialogRef.current?.close();
  }

  const products = order.products;

  return (
    <dialog
      data-assign-dialog
      ref={dialogRef}
      className="assign-delivery-dialog dark:bg-her-blackOlive dark:text-white p-10 rounded-lg"
    >
      <h1 className="text-xl mb-4">{t(msg.delivery.assign)}</h1>
      <form key={formKey} onSubmit={methods.handleSubmit(assign)}>
        <LabelAndSelect
          methods={methods}
          fieldName="assignMode"
          label={t(msg.general.select)}
          topLabelCss="block"
          setDefault={false}
          options={[
            { label: t(msg.delivery.assignModePrepared), value: "prepared" },
            { label: t(msg.delivery.assignModeDraft), value: "draft" },
            { label: t(msg.delivery.assignModeAppend), value: "append" },
          ]}
        />

        {assignMode === "prepared" && (
          <>
            <LabelAndInput
              register={methods.register}
              fieldName="shipDate"
              label={t(msg.posMenuItems.deliveryDate)}
              type="date"
              required={true}
            />

            <LabelAndSelect
              methods={methods}
              fieldName="vehicleId"
              topLabelCss="block"
              label={t(msg.delivery.vehicleName)}
              setDefault={true}
              options={vehicles?.map((v) => ({ label: v.name, value: v.id })) || []}
            />
          </>
        )}

        {assignMode === "append" && (
          <LabelAndSelect
            methods={methods}
            fieldName="appendTrackingId"
            label={t(msg.delivery.selectBatch)}
            topLabelCss="block"
            setDefault={false}
            options={[{ label: "—", value: "" }, ...appendOptions]}
          />
        )}

        <div>
          {products.map((op: OrderProduct) => (
            <div key={op.orderProductId} className="dark:bg-her-darkSectionBg pl-4">
              <FancyCheckbox register={methods.register("orderItemIds")} value={op.orderProductId} defaultChecked={true}>
                <PendingDeliveryOrderItem orderId={order.id} orderProduct={op} />
              </FancyCheckbox>
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-6">
          <button
            type="submit"
            data-assign-submit
            className="bg-her-dodgerblue text-white px-4 py-1 rounded cursor-pointer disabled:bg-her-aaa disabled:cursor-auto disabled:dark:bg-her-lightgray2 disabled:dark:text-her-aaa"
          >
            {t(msg.user.confirm)}
          </button>
          <SolidWeakBtn type="button" onClick={() => dialogRef.current?.close()}>
            {t(msg.form.cancel)}
          </SolidWeakBtn>
        </div>
      </form>
    </dialog>
  );
}

interface PendingDeliveryOrderItemProps {
  orderId: string;
  orderProduct: OrderProduct;
}

function PendingDeliveryOrderItems({ order }: { order: Order }) {
  return (
    <>
      {order.products.map((orderProduct: OrderProduct) => (
        <PendingDeliveryOrderItem
          orderId={order.id}
          key={orderProduct.id} //
          orderProduct={orderProduct}
        />
      ))}
    </>
  );
}

const PendingDeliveryOrderItem = ({ orderId, orderProduct }: PendingDeliveryOrderItemProps) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `${orderId}-${orderProduct.orderProductId}`,
    data: {
      orderItemIds: [orderProduct.orderProductId],
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <label
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="p-4 flex justify-center items-center gap-2 w-full cursor-pointer hover:bg-her-gray/20 text-sm dark:bg-her-darkSectionBg"
    >
      <div className="grow grid grid-cols-[1fr_1fr_auto] gap-2 cursor-pointer">
        <div>
          <div className="flex gap-2 items-center">
            {orderProduct.name} {orderProduct.isGiftProduct && <GiftIcon className="text-her-paradisePink w-6 h-6" />}
            <BundleProductBadge orderProduct={orderProduct} />
          </div>
          {orderProduct.productType === "bundle" && (
            <div className="ml-4 grid grid-cols-2 gap-2">
              {orderProduct.bundleProducts?.map((bp) => (
                <React.Fragment key={bp.orderProductId}>
                  <p data-name={bp.name}>{bp.name}</p>
                  <p>x {bp.quantity}</p>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <p>{formatCurrency(orderProduct.price, orderProduct.currency)}</p>
        <p>x{orderProduct.quantity}</p>
      </div>
    </label>
  );
};
